import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import { Alert, Button, Spinner } from "react-bootstrap";

import { PrintIcon } from "../../utils/components/icons";
import { setAction, getPdf } from "../../reducers/auftrag-reducer";
import { Toast } from "bootstrap";
import { AUFTRAG_STATUS } from "../../utils/enums";
import { sendError, download } from "../../utils/functions";

const aktionLabel = {
  onlineStorno: "Online Storno",
  reprint: "Zweitdruck"
};

const TOAST_TIMEOUT = 5000;

const Aktionen = (props) => {
  const { configStorage, auftragStorage } = useStore().getState();
  const [toast, setToast] = useState(false);
  const [toastText, setToastText] = useState("");
  const [loading, setLoading] = useState(false);
  const [stornoAuftrag, setStornoAuftrag] = useState(null);
  const toastRef = useRef();

  const dispatch = useDispatch();
  const stornoCall = configStorage.getApiRequest("auftragStorno");
  const reprintCall = configStorage.getApiRequest("auftragReprint");
  const auftragCall = configStorage.getApiRequest("auftrag");
  const { auftrag, displayStorno, toastStatus } = props || {};

  const aktion = auftragStorage.getActionStatus();
  const sucessAction = auftragStorage.actionSuccess();
  const stornoSuccess = sucessAction === "onlineStorno";
  const errorAction = auftragStorage.actionError();
  const neuerAuftrag = auftragStorage.isNewAuftrag();

  let stornoType = "onlineStorno";

  const stornoMoeglich = auftrag &&
    auftrag.auftragsStatus !== AUFTRAG_STATUS.ERROR.KEY &&
    auftrag.auftragsStatus !== AUFTRAG_STATUS.ONLINE_STORNO.KEY;

  useEffect(() => {
    if (sucessAction) {
      setToastText(aktionLabel[sucessAction] + " wurde erfolgreich ausgeführt");
    } else if (errorAction) {
      setToastText(errorAction);
    }
  }, [aktion, errorAction, sucessAction]);

  useEffect(() => {
    if (auftrag && stornoAuftrag && auftrag.auftragsNummer !== stornoAuftrag) {
      setStornoAuftrag(null);
      setToastText("");
    }
  }, [auftrag, stornoAuftrag]);

  useEffect(() => {
    const toastElement = toastRef.current;
    if (!toastElement || !toastStatus) return;
    let bsToast = Toast.getInstance(toastElement);
    if (!bsToast) {
      bsToast = new Toast(toastElement, { autohide: false });
      bsToast.hide();
      setToast(false);
    } else {
      toast ? bsToast.show() : bsToast.hide();
    }
  }, [toast, toastStatus]);

  useEffect(() => {
    if (toastText && aktion === "reprint") {
      const timer = setTimeout(() => {
        setToastText("");
        setToast(false);
      }, TOAST_TIMEOUT);
      return () => clearTimeout(timer);
    }
    if (displayStorno) {
      if (!aktion && toastText) setToastText("");
      return;
    }
    if (!aktion || neuerAuftrag) {
      setToastText(aktion?.message || "");
    }
  }, [aktion, displayStorno, neuerAuftrag, toastText]);

  const linkRef = useRef(null);

  const triggerReprint = useCallback(async (e) => {
    const target = e && (e.currentTarget || e.target);
    if (target && auftrag) {
      const auftragsNummer = auftrag.auftragsNummer;
      setToast(toastStatus);
      if (auftragsNummer) {
        setLoading(true);
        const ret = await Promise.resolve(dispatch(getPdf({ apiCall: reprintCall, auftragsNummer })));
        const { error, payload } = ret;
        setLoading(false);
        if (payload && payload.pdf) {
          const nummer = auftrag ? "_" + auftrag.auftragsNummer : "";
          if (linkRef.current) {
            linkRef.current.href = payload.pdf;
            linkRef.current.click();
          } else {
            download(payload.pdf, "reprint" + nummer + ".pdf");
          }
        } else if (error) {
          sendError(error);
          setToastText(error.message);
        }
      }
      if (auftragStorage.getPdf()) {
        linkRef.href = auftragStorage.getPdf();
      }
    }
  }, [auftrag, toastStatus, dispatch, reprintCall, auftragStorage]);

  const triggerStorno = useCallback(async (e) => {
    const target = e && (e.currentTarget || e.target);
    if (target && auftrag) {
      const auftragsNummer = auftrag.auftragsNummer;
      setToast(toastStatus);
      if (auftragsNummer) {
        setLoading(true);
        await Promise.resolve(dispatch(setAction({
          apiCall: stornoCall,
          auftragsNummer,
          stornoText: "",
          bearbeiter: auftrag.bearbeiter,
          aktion: stornoType,
          fetch: auftragCall
        })))
          .then((data) => {
            setStornoAuftrag(auftragsNummer);
            if (data && data.type === "auftrag/setAction/rejected" && data.error) {
              setToastText(data.error.message || aktionLabel[stornoType] + " konnte nicht ausgeführt werden");
            } else if (data && data.type === "auftrag/setAction/fulfilled") {
              setToastText(aktionLabel[stornoType] + " wurde erfolgreich ausgeführt");
            }
          })
          .catch((e) => {
            sendError(e);
            setToastText(auftragStorage.actionError() || "failed");
          });
        setLoading(false);
      }
    }
  }, [auftrag, toastStatus, dispatch, stornoCall, stornoType, auftragCall, auftragStorage]);

  let auftragsNummer = auftrag && auftrag.auftragsNummer;
  const auftragPdfUrl = auftrag && auftrag.pdf;

  if (!auftrag) return;
  const { message, variant } = auftragStorage.getMessage(auftrag.auftragsNummer);

  return <>
    {!toastStatus && message ? <Alert className="p-1" variant={variant}>{message}</Alert> : null}
    {loading
      ? <Spinner animation="border" role="status" variant="primary">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
      : null
    }
    <div roll="group">
      {displayStorno
        ? <Button
          className="aktion pt-0 bp-0 me-1"
          title={aktionLabel[stornoType]}
          aria-label={aktionLabel[stornoType]}
          variant="danger"
          disabled={toast || stornoSuccess || loading || !stornoMoeglich}
          onClick={triggerStorno}
        >
          Storno
        </Button>
        : null
      }
      {auftragsNummer && auftragPdfUrl
        ? <a
          className="btn btn-info p-0"
          ref={linkRef}
          title={"PDF des Auftrags " + auftragsNummer}
          href={auftragPdfUrl}
          download={"reprint_" + auftragsNummer + ".pdf"}
        >
          <PrintIcon></PrintIcon>
        </a>
        : <Button
          className="aktion p-0"
          title={aktionLabel["reprint"] + " ausführen"}
          aria-label={aktionLabel["reprint"] + " ausführen"}
          variant="info"
          disabled={toast || loading}
          onClick={triggerReprint}
        >
          <PrintIcon></PrintIcon>
        </Button>
      }
    </div>
    {toastStatus
      ? <div className="toast position-absolute" role="status" ref={toastRef}>
        <div className="toast-header">
          <strong className="me-auto">{toastText}</strong>
          <button type="button" className="btn-close" onClick={() => setToast(false)} aria-label="Close"></button>
        </div>
      </div>
      : null
    }
  </>;
};
export default Aktionen;
